import { Inject, Injectable, Optional } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  MtnaInMemorySearchFacade,
  MtnaSearchDialogComponent,
  MtnaSearchDialogData,
  MtnaSearchDialogOutput,
} from '@mtna/search-manager-angular';
import { RdsApiService } from '@rds/shared-api';
import { Observable, Subject } from 'rxjs';

import { RdsDataProductSelection } from '../core/models';
import { RDS_DATA_PRODUCT_DIALOG_OPTIONS, RdsDataProductDialogOptions } from './data-product-dialog-options-token';

/**
 * Central service for managing the data product selection dialog.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Injectable({ providedIn: 'root' })
export class RdsDataProductDialogService {
  private selectDialogRef: MatDialogRef<MtnaSearchDialogComponent<RdsDataProductSelection>> | undefined;

  constructor(
    private dialog: MatDialog,
    private rdsApiService: RdsApiService,
    private searchFacade: MtnaInMemorySearchFacade,
    @Optional() @Inject(RDS_DATA_PRODUCT_DIALOG_OPTIONS) private options?: RdsDataProductDialogOptions
  ) {}

  /**
   * Open a select data source dialog if one is not already opened.
   *
   * @param forceSelection Requires a selection to be made before closing, defaults to false
   * @param dialogWidth dialog width, defaults to 80vw
   * @returns Observable that emits the dialog output and whether a different data product was selected.
   */
  openSelectDataSourceDialog(
    forceSelection: boolean = false,
    dialogWidth: string = '80vw',
    columnWidths?: {
      [id: string]: number;
    }
  ): Observable<MtnaSearchDialogOutput<RdsDataProductSelection> & { changed: boolean }> {
    const sub = new Subject<MtnaSearchDialogOutput<RdsDataProductSelection> & { changed: boolean }>();
    if (!this.selectDialogRef) {
      // If the dialog is not already open

      // If a catalog is set, get the ID
      const currentCatalogId = this.rdsApiService.isCatalogIdSet() ? this.rdsApiService.getCatalogId() : undefined;
      // If a data product is set, get the ID
      const currentDataProductId = this.rdsApiService.isDataProductIdSet() ? this.rdsApiService.getDataProductId() : undefined;
      const selection: RdsDataProductSelection | undefined =
        currentCatalogId && currentDataProductId ? { catalogId: currentCatalogId, dataProductId: currentDataProductId } : undefined;

      this.selectDialogRef = this.dialog.open<MtnaSearchDialogComponent<RdsDataProductSelection>>(MtnaSearchDialogComponent, {
        disableClose: forceSelection,
        width: dialogWidth,
        data: new MtnaSearchDialogData(
          this.searchFacade,
          $localize`Select a Data Product`,
          !forceSelection,
          {},
          undefined,
          true,
          {
            color: 'primary',
            disableSubResults: true,
            resizableColumns: false,
            selectable: true,
            columnWidths: columnWidths || this.options?.columnWidths,
          },
          selection,
          this.options?.otherButtons
        ),
      });
      this.selectDialogRef.afterClosed().subscribe((output: MtnaSearchDialogOutput<RdsDataProductSelection>) => {
        if (output && output.selected) {
          // If there is an output, a selection was made,
          // check if the diloag selection is different than the current selection
          const changed: boolean = output.selected.catalogId !== currentCatalogId || output.selected.dataProductId !== currentDataProductId;
          if (changed) {
            // If the user selected something different, set those values
            this.rdsApiService.setCatalogId(output.selected.catalogId);
            this.rdsApiService.setDataProductId(output.selected.dataProductId);
          }
          sub.next({ ...output, changed });
        } else {
          sub.next({ ...output, changed: false });
        }
        sub.complete();
        this.selectDialogRef = undefined;
      });
    } else {
      // The dialog is already opened, do nothing.
      sub.complete();
    }
    return sub.asObservable();
  }
}
